import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRightIcon, RefreshCwIcon, ShieldCheckIcon, SparklesIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { detailQuestions, statusLabels } from '../data/demandes';
import { useRequests } from '../contexts/RequestsContext';
import { StatusProgress } from '../components/demandes/RequestList';
import { RequestDetailsPanel } from '../components/demandes/RequestDetailsPanel';
import { RequestMessages } from '../components/demandes/RequestMessages';
const answers: Record<string, string> = {
  'Où en est ma demande ?': 'Votre demande est en traitement par le service des travaux publics depuis le 28 août.',
  'Quand sera-t-elle traitée ?': 'Aucune date d’exécution n’est disponible pour le moment. Vous serez avisé dès que le statut change.',
  'Est-ce que je dois faire autre chose ?': 'Non, aucune action n’est requise de votre part pour l’instant.'
};
type Tab = 'details' | 'messages';
export function DemandeDetail() {
  const {
    id
  } = useParams();
  const {
    requests
  } = useRequests();
  const request = requests.find(item => item.id === id) ?? requests[0];
  const [tab, setTab] = useState<Tab>('details');
  const [asked, setAsked] = useState<string | null>(null);
  const pending = request.messages.filter(message => message.author === 'ville' && message.awaiting).length;
  const tabs: {
    id: Tab;
    label: string;
    badge?: number;
  }[] = [{
    id: 'details',
    label: 'Détails'
  }, {
    id: 'messages',
    label: 'Messages',
    badge: pending || undefined
  }];
  return <main className="bg-white" data-id="element-2834" data-mp="2w8a9z39kfbes">
      <div className="mx-auto max-w-[900px] px-5 py-12 lg:px-10 lg:py-16" data-id="element-2835" data-mp="2w78vspx968su">
        <nav aria-label="Fil d’Ariane" className="flex flex-wrap items-center gap-1.5 text-sm text-slate-500" data-id="element-2836" data-mp="2r9p5usr707l2">
          <Link to="/" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-2837" data-mp="1ktdg9g2fry13">
            Accueil
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2838" data-mp="1fsh1u8kgkthf" />
          <Link to="/mes-demandes" className="font-semibold text-ville-deep hover:text-ville-indigo" data-id="element-2839" data-mp="7ubl343ozhni">
            Mes demandes
          </Link>
          <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2840" data-mp="bvdkpm1itvq0" />
          <span aria-current="page" data-id="element-2841" data-mp="3u0qdgnp282sy">{request.reference}</span>
        </nav>

        <header className="mt-4" data-id="element-2842" data-mp="2uyymkgqgclg">
          <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-2843" data-mp="2u76jjmcgwzx">
            <div data-id="element-2844" data-mp="3kbqdp22qh3dp">
              <p className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wider text-ville-deep" data-id="element-2845" data-mp="3kb6uli1ck8y3">
                <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-2846" data-mp="3p3zzc5wtfha6" />
                {request.service}
              </p>
              <h1 className="mt-2 text-4xl font-extrabold tracking-tight text-ville-navy" data-id="element-2847" data-mp="3gaej0i505ryn">{request.title}</h1>
              <p className="mt-1 text-sm font-semibold text-slate-500" data-id="element-2848" data-mp="31de1f7gizddw">
                {request.category} · Requête {request.reference}
              </p>
            </div>
            <button type="button" aria-label="Actualiser la requête" className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-2849" data-mp="36ebjzl5g69fx">
              <RefreshCwIcon className="h-4 w-4" aria-hidden="true" data-id="element-2850" data-mp="35ohtxcvczpq4" />
              Actualiser
            </button>
          </div>

          <div className="mt-5" data-id="element-2851" data-mp="31n6chumn2at0">
            <StatusProgress status={request.status} data-id="element-2852" data-mp="31lv75qdjouzs" />
          </div>
        </header>

        <div role="tablist" aria-label="Sections de la requête" className="mt-8 flex gap-6 border-b border-slate-200" data-id="element-2853" data-mp="3fh71vxrpalgp">
          {tabs.map(item => {
          const active = tab === item.id;
          return <button key={item.id} type="button" role="tab" id={`onglet-${item.id}`} aria-selected={active} aria-controls={`panneau-${item.id}`} onClick={() => setTab(item.id)} className={`relative -mb-px flex items-center gap-2 pb-3 text-[15px] font-extrabold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'text-ville-navy' : 'text-slate-500 hover:text-ville-navy'}`} data-id="element-2854" data-mp="3ke5xyluxd2hj">
                {item.label}
                {item.badge && <span className="grid h-5 min-w-5 place-items-center rounded-full bg-ville-lime px-1.5 text-[11px] font-bold text-ville-navy" data-id="element-2855" data-mp="18qtzpaeygoau">
                    {item.badge}
                  </span>}
                {active && <motion.span layoutId="onglet-demande" transition={{
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-ville-navy" aria-hidden="true" data-id="element-2856" data-mp="2k57iw1yrkfkj" />}
              </button>;
        })}
        </div>

        {tab === 'details' ? <div role="tabpanel" id="panneau-details" aria-labelledby="onglet-details" className="mt-6" data-id="element-2857" data-mp="3q2ttkvteoam">
            <RequestDetailsPanel request={request} data-id="element-2858" data-mp="3uxykcs8a8hsy" />

            <p className="mt-4 text-xs text-slate-500" data-id="element-2859" data-mp="3pzdsph73j6g4">Statut actuel : {statusLabels[request.status]}</p>

            <section aria-labelledby="atna-demande-titre" className="mt-6 rounded-3xl border border-slate-200 p-6 sm:p-7" data-id="element-2860" data-mp="3lxackf9hfdoi">
              <h2 id="atna-demande-titre" className="flex items-center gap-2 text-[15px] font-extrabold text-ville-navy" data-id="element-2861" data-mp="3gyz4oa2qzd8x">
                <SparklesIcon className="h-5 w-5 text-ville-indigo" aria-hidden="true" data-id="element-2862" data-mp="3c1grg7hp6bv4" />
                Demander à ATNA
              </h2>

              <ul className="mt-4 flex flex-wrap gap-2" data-id="element-2863" data-mp="1a7hhpqk842kj">
                {detailQuestions.map(question => <li key={question} data-id="element-2864" data-mp="34n6ivn7aipgn">
                    <button type="button" onClick={() => setAsked(question)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${asked === question ? 'bg-ville-indigo text-white' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-2865" data-mp="34m4ukh19k6gz">
                      {question}
                    </button>
                  </li>)}
              </ul>

              {asked && <p className="mt-4 rounded-2xl bg-ville-indigo-soft px-5 py-4 text-sm leading-relaxed text-ville-navy" data-id="element-2866" data-mp="221mkf8lp6ps7">
                  {answers[asked]}
                </p>}

              <p className="mt-4 text-xs text-slate-500" data-id="element-2867" data-mp="p2km4nrnptae">
                Les mises à jour de cette demande apparaissent aussi dans{' '}
                <Link to="/notifications" className="font-bold text-ville-indigo hover:underline" data-id="element-2868" data-mp="k2okuwacgfyj">
                  Notifications
                </Link>
                .
              </p>
            </section>
          </div> : <div role="tabpanel" id="panneau-messages" aria-labelledby="onglet-messages" className="mt-6" data-id="element-2869" data-mp="tzt9r3vkqpj1">
            <RequestMessages request={request} data-id="element-2870" data-mp="69ys890b8csg" />
          </div>}
      </div>
    </main>;
}